<template>
    <b-overlay
        :show="showOverlay"
        rounded="sm"
        spinner-variant="primary"
        spinner-small
    >
        <b-row class="pb-2">
            <b-col>
                <h4 v-if="null === id">
                    {{ $t('Page(UserCard):Component(Text):add-new-user-label') }}
                </h4>
                <h4 v-else>
                    {{ $t('Page(UserCard):Component(Text):edit-user-label', {user: card.name}) }}
                </h4>
            </b-col>
            <b-col md="6" lg="6">
                <AddEditActions
                    class="float-right"
                    @save="save"
                    @close="close"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col md="6" lg="6">
                <b-card no-body>
                    <div class="card-header">
                        <div class="col-md-12 pl-0">
                            <b-card-title>
                                {{ $t('Page(UserCard):Component(Text):general-info') }}
                            </b-card-title>
                        </div>
                    </div>
                    <div class="p-2">
                        <validation-observer ref="generalInfo">
                            <b-form-group
                                :label="$t('Page(UserCard):Component(Label):name')"
                                label-for="name"
                            >
                                <validation-provider
                                    #default="{ errors }"
                                    :name="$t('Page(UserCard):Component(Label):name')"
                                    rules="required"
                                >
                                    <b-form-input
                                        :state="errors.length > 0 ? false:null"
                                        id="name"
                                        v-model="card.name"
                                        :placeholder="$t('Page(UserCard):Component(Placeholder):name')"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>

                            <b-form-group
                                :label="$t('Page(UserCard):Component(Label):position')"
                                label-for="position"
                            >
                                <b-form-input
                                    id="position"
                                    v-model="card.position"
                                    :placeholder="$t('Page(UserCard):Component(Placeholder):position')"
                                />
                            </b-form-group>

                            <b-form-group
                                :label="$t('Component(Label):description')"
                                label-for="description"
                            >
                                <b-form-input
                                    id="description"
                                    v-model="card.description"
                                    :placeholder="$t('Component(Placeholder):description')"
                                />
                            </b-form-group>
                        </validation-observer>
                    </div>
                </b-card>
            </b-col>
            <b-col md="6" lg="6">
                <b-card no-body>
                    <div class="card-header">
                        <div class="col-md-12 pl-0">
                            <b-card-title>
                                {{ $t('Page(UserCard):Component(Text):account-info') }}
                            </b-card-title>
                        </div>
                    </div>
                    <div class="p-2">
                        <validation-observer ref="accountInfo">
                            <PhoneNumberInput
                                :default-country-code="card.phone.countryCode"
                                :phone="card.phone.number"
                                @input="onPhoneInput"
                            />

                            <b-form-group
                                :label="$t('Component(Label):email')"
                                label-for="email"
                            >
                                <validation-provider
                                    #default="{ errors }"
                                    :name="$t('Component(Label):email')"
                                    rules="required|email"
                                >
                                    <b-form-input
                                        :state="errors.length > 0 ? false:null"
                                        id="email"
                                        v-model="card.email"
                                        :placeholder="$t('Component(Placeholder):email')"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>

                            <b-form-group
                                :label="$t('Component(Label):password')"
                                label-for="password"
                            >
                                <validation-provider
                                    #default="{ errors }"
                                    :name="$t('Component(Label):password')"
                                    vid="password"
                                    :rules="this.id ? 'password' : 'required|password'"
                                >
                                    <b-form-input
                                        :state="errors.length > 0 ? false:null"
                                        id="password"
                                        type="password"
                                        v-model="passwordValue"
                                        @input="onPasswordChange"
                                        :placeholder="$t('Component(Placeholder):password')"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>

                            <b-form-group
                                :label="$t('Component(Label):confirm-password')"
                                label-for="confirm-password"
                            >
                                <validation-provider
                                    #default="{ errors }"
                                    :name="$t('Component(Label):confirm-password')"
                                    :rules="this.id ? 'confirmed:password' : 'required|confirmed:password'"
                                >
                                    <b-form-input
                                        :state="errors.length > 0 ? false:null"
                                        id="confirm-password"
                                        type="password"
                                        v-model="passwordValueC"
                                        :placeholder="$t('Component(Placeholder):confirm-password')"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </validation-observer>
                    </div>
                </b-card>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-card no-body>
                    <div class="card-header">
                        <div class="col-md-6 pl-0">
                            <b-card-title>{{ $t('Page(UserCard):Component(Text):permissions') }}</b-card-title>
                        </div>
                        <div class="col-md-6 pr-0">
                            <div class="float-right">
                                <b-button
                                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                    variant="success"
                                    class="btn-icon rounded-circle"
                                    @click="addPermission"
                                >
                                    <feather-icon icon="PlusIcon" />
                                </b-button>
                            </div>
                        </div>
                    </div>
                    <div class="pl-2 pr-2 pb-2">
                        <Transition name="fade">
                            <b-alert
                                :show="showPermissionRequiredMessage"
                                variant="danger"
                                class="mb-50"
                            >
                                <div class="alert-body">
                                    {{ $t('Component(ValiadtionMessage):user-permission-is-required') }}
                                </div>
                            </b-alert>
                        </Transition>
                        <Permission
                            v-for="(permission, index) in card.permissions"
                            :organizations="organizations"
                            class="mb-50"
                            :roles="roles"
                            :index="index"
                            :permission="permission"
                            :show-delete-button="isPermissionManagable(permission)"
                            :is-managable="isPermissionManagable(permission)"
                            ref="permissions"
                            @change="onPermissionChange"
                            @remove="remove"
                        />
                    </div>
                </b-card>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <AddEditActions
                    class="float-right"
                    @save="save"
                    @close="close"
                />
            </b-col>
        </b-row>
    </b-overlay>
</template>

<script>
import {
    BCard, BCardTitle, BButton, BRow, BCol, BOverlay, BFormGroup, BFormInput, BAlert
} from 'bootstrap-vue'
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
    required, email, confirmed, password,
} from '@validations'
import PhoneNumberInput from "@core/components/common/PhoneNumberInput.vue";
import AddEditActions from "@/views/administration/user/components/AddEditActions";
import Permission from "@/views/administration/user/components/Permission";
import Ripple from 'vue-ripple-directive'
import {SupplyChain, UserApi} from "@core/api";
import backendResponse from "@core/mixins/ui/backend-response";

export default {
    mixins: [backendResponse],
    components: {
        BOverlay, BCard, BButton, BRow, BCol, BCardTitle, PhoneNumberInput, BFormGroup, BFormInput,
        ValidationProvider, ValidationObserver, AddEditActions, Permission, BAlert
    },
    directives: {
        Ripple
    },
    props: {
        id: {
            default: null
        }
    },
    data() {
        return {
            showOverlay: true,
            card: {
                phone: {
                    countryPhoneCode: null,
                    countryCode: null,
                    number: null
                },
                name: '',
                email: '',
                position: '',
                description: '',
                permissions: [],
            },
            showPermissionRequiredMessage: false,
            passwordValue: null,
            passwordValueC: null,
            organizations: [],
            roles: [],

            required,
            password,
            email,
            confirmed,
        };
    },
    methods: {
        onPhoneInput(data) {
            if (data.formatInternational) {
                this.card.phone.countryPhoneCode = data.countryPhoneCode;
                this.card.phone.countryCode = data.countryCode;
                this.card.phone.number = data.formatInternational;
            }
        },
        onPasswordChange(password) {
            this.card.password = password;
        },
        validatePermissions() {
            return new Promise((resolve, reject) => {
                if (this.card.permissions.length === 0) {
                    this.showPermissionRequiredMessage = true;
                    resolve(false);
                } else {
                    this.showPermissionRequiredMessage = false;
                    let valid = true;
                    this.card.permissions.forEach((permission, index) => {
                        if (!this.$refs['permissions'][index].isValid()) {
                            valid = false;
                        }
                    });

                    resolve(valid);
                }
            });
        },
        gatherSaveData() {
            let data = {
                ...this.card,
                permissions: this.card.permissions.map(p => {
                    let permission = {
                        organization: p.organization.id,
                        role: p.role.id,
                        validFrom: this.$moment.utc(p.validFrom).format('YYYY-MM-DD HH:mm:ss'),
                        validTo: this.$moment.utc(p.validTo).format('YYYY-MM-DD HH:mm:ss'),
                    };

                    if (p.id) {
                        permission.id = p.id;
                    }

                    return permission;
                })
            };
            if (!data.phone.number) {
                delete data.phone;
            }
            if (data.id) {
                delete data.id;
            }

            return data;
        },
        save() {
            Promise.all([
                this.$refs.generalInfo.validate(),
                this.$refs.accountInfo.validate(),
                this.validatePermissions()
            ]).then(validationResults => {
                if (validationResults.includes(false)) {
                    return;
                }
                this.showOverlay = true;
                UserApi
                    .saveCard(this.id, this.gatherSaveData())
                    .then((r) => {
                        this.passwordValue = this.passwordValueC = null;
                        this.showDefaultSuccessMessage('User has been successfully created');

                        if (!this.id) {
                            this.$router.push({
                                name: 'administration-users',
                            });
                        }
                    })
                    .catch(this.handleBackendError)
                    .finally(() => {
                        this.showOverlay = false;
                    })
                ;
            });
        },
        close() {
            this.$router.go(-1);
        },
        addPermission() {
            this.card.permissions.push({
                organization: null,
                role: null,
                validFrom: new Date(),
                validTo: new Date('2099-01-01'),
                new: true
            });
            this.showPermissionRequiredMessage = false;
        },
        onPermissionChange(permission, index) {
            this.card.permissions[index].organization = permission.organization;
            this.card.permissions[index].role = permission.role;
            this.card.permissions[index].validFrom = permission.validFrom;
            this.card.permissions[index].validTo = permission.validTo;
        },
        remove(permission, index) {
            this.card.permissions.splice(index, 1);
        },
        isPermissionManagable(permission) {
            return true;
            if (permission.new) {
                return true;
            }
            return this.$moment(new Date()).isAfter(permission.validFrom) === false;
        },
        fetchUserCard() {
            return this.id ? UserApi.getCard(this.id).then(r => {
                this.card = r.data;
            }) : true;
        }
    },
    created() {
        Promise.all([
            this.fetchUserCard(),
            SupplyChain.getOrganizations().then(r => {
                this.organizations = r.data;
            }),
            UserApi.getRoles().then(r => {
                this.roles = r.data;
            })
        ]).then(() => {
            this.showOverlay = false;
        })
    },
    watch: {
        id() {
            if (this.id && !this.card.id) {
                this.fetchUserCard();
            }
        }
    }
}
</script>
